/*! Meller Room — interactive 2.5D scene engine (no dependencies) */
(function (global) {
  'use strict';

  var clamp = function (v, a, b) { return Math.max(a, Math.min(b, v)); };
  var lerp = function (a, b, t) { return a + (b - a) * t; };
  var easeInOut = function (t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; };
  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };
  var ICON = {
    close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    prev: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>',
    next: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>',
    arrow: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>',
    fullscreen: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5"/></svg>'
  };
  var MOBILE_MQ = '(max-width: 760px), (max-height: 520px) and (max-width: 960px)';
  // Mobile bottom sheet, as fractions of the room's height (keep in sync with meller-room.css):
  // it is SHEET_FULL tall and opens pushed down by SHEET_OFF of itself, so SHEET_PEEK shows.
  var SHEET_FULL = 0.8, SHEET_OFF = 0.225, SHEET_PEEK = SHEET_FULL * (1 - SHEET_OFF);

  function h(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  function youtubeId(u) {
    if (!u) return '';
    var m = String(u).match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{6,})/);
    return m ? m[1] : (/^[\w-]{6,}$/.test(u) ? u : '');
  }
  function vimeoId(u) {
    var m = String(u || '').match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m ? m[1] : (/^\d+$/.test(u) ? u : '');
  }

  function normalizeContent(c) {
    c = c || {};
    var s = c.settings || {};
    return {
      settings: {
        eyebrow: s.eyebrow || '',
        title: s.title || '',
        subtitle: s.subtitle || '',
        subtitleTouch: s.subtitleTouch || s.subtitle || '',
        hint: s.hint || '',
        hintTouch: s.hintTouch || s.hint || '',
        labels: !!s.labels,
        parallax: s.parallax !== false,
        accent: s.accent || '',
        hotspotColor: s.hotspotColor || '',
        background: s.background || '',
        bgTransparent: !!s.bgTransparent,
        float: s.float !== false
      },
      hotspots: (c.hotspots || []).map(function (x, i) {
        return {
          id: x.id || ('h' + i),
          object: x.object || '',
          label: x.label || x.title || ('Punto ' + (i + 1)),
          x: +x.x || 50,
          y: +x.y || 50,
          zoom: +x.zoom || 0,
          full: !!x.full,
          eyebrow: x.eyebrow || '',
          title: x.title || '',
          body: x.body || '',
          media: Array.isArray(x.media) ? x.media.filter(function (m) { return m && (m.src || m.url); }) : [],
          ctas: Array.isArray(x.ctas) ? x.ctas.filter(function (b) { return b && b.label; }) : []
        };
      })
    };
  }

  /* ---------------- WebGL depth renderer ---------------- */
  var VS = 'attribute vec2 aPos;uniform vec2 uRes;varying vec2 vPos;' +
    'void main(){vPos=vec2((aPos.x*.5+.5)*uRes.x,(.5-aPos.y*.5)*uRes.y);gl_Position=vec4(aPos,0.,1.);}';
  var FS = '#ifdef GL_FRAGMENT_PRECISION_HIGH\nprecision highp float;\n#else\nprecision mediump float;\n#endif\n' +
    'uniform sampler2D uImg;uniform sampler2D uDepth;uniform vec2 uViewC;uniform vec3 uCam;uniform vec2 uScene;' +
    'uniform vec2 uPar;uniform float uFocus;uniform float uDim;uniform float uBlur;varying vec2 vPos;' +
    'void main(){vec2 uv=((vPos-uViewC)/uCam.z+uCam.xy)/uScene;vec2 u=uv;' +
    'for(int i=0;i<5;i++){float d=texture2D(uDepth,u).r;u=uv-(d-uFocus)*uPar;}' +
    // sample before the edge test: a texture read inside a branch has no reliable
    // mip level along the scene edge and drew a 1px line of the smallest mip there
    'vec4 c=texture2D(uImg,u,uBlur);' +
    'if(u.x<0.||u.y<0.||u.x>1.||u.y>1.){gl_FragColor=vec4(0.);return;}' +
    'float g=dot(c.rgb,vec3(.299,.587,.114));' +
    'c.rgb=mix(c.rgb,vec3(g),.22*uDim)*(1.-.56*uDim);gl_FragColor=c;}';

  function GLView(canvas) {
    var opts = { antialias: false, alpha: true, depth: false, stencil: false, premultipliedAlpha: true, powerPreference: 'high-performance' };
    var gl = canvas.getContext('webgl2', opts);
    this.gl2 = !!gl;
    if (!gl) gl = canvas.getContext('webgl', opts) || canvas.getContext('experimental-webgl', opts);
    if (!gl) throw new Error('no webgl');
    this.gl = gl;
    var sh = function (type, src) {
      var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
      return s;
    };
    var p = gl.createProgram();
    gl.attachShader(p, sh(gl.VERTEX_SHADER, VS));
    gl.attachShader(p, sh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error('link');
    gl.useProgram(p);
    this.p = p;
    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(p, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    var u = {};
    ['uImg', 'uDepth', 'uRes', 'uViewC', 'uCam', 'uScene', 'uPar', 'uFocus', 'uDim', 'uBlur'].forEach(function (n) { u[n] = gl.getUniformLocation(p, n); });
    this.u = u;
    gl.uniform1i(u.uImg, 0);
    gl.uniform1i(u.uDepth, 1);
    gl.clearColor(0, 0, 0, 0);
  }
  GLView.prototype.solid = function (unit, rgb) {
    var gl = this.gl, t = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([rgb[0], rgb[1], rgb[2], 255]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return t;
  };
  GLView.prototype.texture = function (unit, img, mip, premult) {
    var gl = this.gl;
    var t = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, !!premult);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    var pot = function (n) { return (n & (n - 1)) === 0; };
    if (mip && (this.gl2 || (pot(img.naturalWidth || img.width) && pot(img.naturalHeight || img.height)))) {
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    } else {
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    }
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    var err = gl.getError();
    if (err && err !== gl.NO_ERROR) throw new Error('texture ' + err);
    return t;
  };
  GLView.prototype.draw = function (w, h, cam, scene, par, focus, dim, blur) {
    var gl = this.gl, u = this.u;
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.uniform2f(u.uRes, w, h);
    gl.uniform2f(u.uViewC, cam.vx, cam.vy);
    gl.uniform3f(u.uCam, cam.cx, cam.cy, cam.s);
    gl.uniform2f(u.uScene, scene.w, scene.h);
    gl.uniform2f(u.uPar, par.x, par.y);
    gl.uniform1f(u.uFocus, focus);
    gl.uniform1f(u.uDim, dim || 0);
    gl.uniform1f(u.uBlur, blur || 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  };

  /* ---------------- Room ---------------- */
  function Room(root, o) {
    if (!(this instanceof Room)) return new Room(root, o);
    this.root = root;
    this.scene = o.scene;
    this.resolve = o.resolve || function (s) { return s; };
    this.content = normalizeContent(o.content);
    this.editable = !!o.editable;
    this.onChange = o.onChange || null;
    this.onOpen = o.onOpen || null;
    this.onClose = o.onClose || null;
    this.strings = Object.assign({ close: 'Cerrar', prev: 'Anterior', next: 'Siguiente', loading: 'Cargando la habitación', video: 'Vídeo', fullscreen: 'Pantalla completa', of: '/' }, o.strings || {});
    this.W = this.scene.w; this.H = this.scene.h;
    this.mqMobile = window.matchMedia(MOBILE_MQ);
    this.mqHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    // iOS / iPadOS (every browser there is WebKit): the camera stays put when a pop-up
    // opens. Moving the scene frame by frame, even a pan with no zoom, got the page
    // killed on iPhones; with a still camera it never was. iPads in desktop mode report
    // as a Mac with touch points. Android and desktop keep the zoom.
    this.stillCam = /iP(hone|od|ad)/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
    this.cam = { cx: this.W / 2, cy: this.H / 2, s: 0.3, vx: 0, vy: 0 };
    this.tw = null;
    this.par = { x: 0, y: 0 }; this.parT = { x: 0, y: 0 };
    this.mouse = { x: 0, y: 0, in: false };
    this.focus = this.scene.focus || 0.55; this.focusT = this.focus;
    this.dim = 0; this.dimT = 0; this._lastDim = -1;
    this.active = null; this.hover = null;
    this.objEls = {};
    this.running = false; this.visible = true;
    this._t = 0;
    this._build();
    this._applyTheme();
    this._buildObjects();
    this._renderHotspots();
    this._renderIntro();
    this._bind();
    this._resize(true);
    this._load();
  }

  function hexRgb(hex) {
    var m = String(hex || '').trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!m) return null;
    var x = m[1].length === 3 ? m[1].replace(/./g, '$&$&') : m[1];
    return [parseInt(x.slice(0, 2), 16), parseInt(x.slice(2, 4), 16), parseInt(x.slice(4, 6), 16)];
  }
  Room.prototype._applyTheme = function () {
    var s = this.content.settings, st = this.root.style;
    st.setProperty('--mr-accent', s.accent || '');
    st.setProperty('--mr-hs', s.hotspotColor || '');
    var bg = hexRgb(s.background);
    if (bg) {
      var mix = function (c, t, k) { return Math.round(c + (t - c) * k); };
      st.setProperty('--mr-bg', 'rgb(' + bg.join(',') + ')');
      st.setProperty('--mr-bg-deep', 'rgb(' + bg.map(function (c) { return mix(c, 0, 0.5); }).join(',') + ')');
      st.setProperty('--mr-bg-glow', 'rgba(' + bg.map(function (c) { return mix(c, 255, 0.35); }).join(',') + ',.28)');
    } else { st.removeProperty('--mr-bg'); st.removeProperty('--mr-bg-deep'); st.removeProperty('--mr-bg-glow'); }
    this.root.classList.toggle('bg-transparent', !!s.bgTransparent);
    this.root.classList.toggle('no-float', s.float === false);
    this.root.classList.toggle('labels-on', !!s.labels);
  };

  Room.prototype._build = function () {
    var r = this.root, S = this.strings;
    r.classList.remove('is-editing', 'is-open', 'has-object', 'gl-ready', 'is-ready', 'has-interacted', 'labels-on');
    r.classList.add('mr');
    r.classList.toggle('is-editing', this.editable);
    // lighter layers on iOS, see .mr.is-still in meller-room.css
    r.classList.toggle('is-still', this.stillCam);
    r.style.setProperty('--mr-dim', '0');
    r.innerHTML = '';
    this.viewport = h('div', 'mr-viewport');
    var mkWorld = function (cls) {
      var w = h('div', 'mr-world ' + cls);
      w.style.width = this.W + 'px'; w.style.height = this.H + 'px';
      return w;
    }.bind(this);
    // floor shadow (stays on the ground while the room floats)
    this.floorWorld = mkWorld('mr-world-floor');
    if (this.scene.room && this.scene.room.shadow) {
      var rs = this.scene.room.shadow, fs = h('img', 'mr-floor-shadow');
      fs.alt = ''; fs.decoding = 'async'; fs.src = this.resolve(rs.src);
      fs.style.left = rs.x + 'px'; fs.style.top = rs.y + 'px'; fs.style.width = rs.w + 'px'; fs.style.height = rs.h + 'px';
      this.floorWorld.appendChild(fs);
    }
    this.floatEl = h('div', 'mr-float');
    this.backEl = h('div', 'mr-back');
    this.canvas = h('canvas', 'mr-gl');
    this.canvas.setAttribute('aria-hidden', 'true');
    this.worldBack = mkWorld('mr-world-back');
    this.baseImg = h('img', 'mr-base is-preview');
    this.baseImg.alt = '';
    this.baseImg.decoding = 'async';
    if (this.scene.preview) this.baseImg.src = this.scene.preview;
    this.layUnder = h('div', 'mr-layer mr-unders');
    this.worldBack.append(this.baseImg, this.layUnder);
    this.backEl.append(this.canvas, this.worldBack);
    this.world = mkWorld('mr-world-front');
    this.layShadow = h('div', 'mr-layer mr-shadows');
    this.layCut = h('div', 'mr-layer mr-cuts');
    this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.svg.setAttribute('class', 'mr-hit');
    this.svg.setAttribute('viewBox', '0 0 ' + this.W + ' ' + this.H);
    this.svg.setAttribute('width', this.W);
    this.svg.setAttribute('height', this.H);
    this.world.append(this.layShadow, this.layCut, this.svg);
    this.hsLayer = h('div', 'mr-hotspots');
    this.floatEl.append(this.backEl, this.world, this.hsLayer);
    this.viewport.append(this.floorWorld, this.floatEl);

    this.intro = h('header', 'mr-intro');
    this.hint = h('div', 'mr-hint');
    this.hint.setAttribute('aria-hidden', 'true');

    this.panel = h('aside', 'mr-panel');
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'false');
    this.panel.setAttribute('aria-labelledby', 'mr-title-' + (this.root.id || 'room'));
    this.panel.innerHTML =
      '<div class="mr-panel-grip"></div>' +
      '<div class="mr-panel-bar"><span class="mr-count"></span>' +
      '<button type="button" class="mr-iconbtn mr-prev" aria-label="' + esc(S.prev) + '">' + ICON.prev + '</button>' +
      '<button type="button" class="mr-iconbtn mr-next" aria-label="' + esc(S.next) + '">' + ICON.next + '</button>' +
      '<button type="button" class="mr-iconbtn mr-close" aria-label="' + esc(S.close) + '">' + ICON.close + '</button></div>' +
      '<div class="mr-panel-scroll"></div>';
    this.panelScroll = this.panel.querySelector('.mr-panel-scroll');
    this.panelCount = this.panel.querySelector('.mr-count');

    this.loader = h('div', 'mr-loader', '<div class="mr-loader-box"><span>' + esc(S.loading) + '</span><div class="mr-loader-bar"><i></i></div></div>');
    this.loaderBar = this.loader.querySelector('i');
    r.append(this.viewport, this.intro, this.hint, this.panel, this.loader);
  };

  Room.prototype._buildObjects = function () {
    var self = this, sc = this.scene, R = this.resolve;
    var mk = function (layer, cls, spec) {
      var g = h('div', 'mr-pg');
      var im = h('img', cls);
      im.alt = ''; im.decoding = 'async';
      // set in _ready / on first hover, so the base image gets the bandwidth first
      im.dataset.src = R(spec.src);
      im.style.left = spec.x + 'px'; im.style.top = spec.y + 'px';
      im.style.width = spec.w + 'px'; im.style.height = spec.h + 'px';
      g.appendChild(im); layer.appendChild(g);
      return { g: g, img: im };
    };
    var all = Object.assign({}, sc.extras || {}, sc.objects || {});
    Object.keys(all).forEach(function (id) {
      var o = all[id];
      var rec = { id: id, o: o, interactive: !!(sc.objects && sc.objects[id]) };
      if (o.under) rec.under = mk(self.layUnder, 'mr-under', o.under);
      if (o.shadow) {
        rec.shadow = mk(self.layShadow, 'mr-shadow kind-' + (o.shadow.kind || 'wall'), o.shadow);
        rec.shadow.img.style.setProperty('--sdy', Math.round((o.lift || 20) * 0.55) + 'px');
      }
      if (o.cut) {
        var c = h('div', 'mr-cut');
        c.style.left = o.cut.x + 'px'; c.style.top = o.cut.y + 'px';
        c.style.width = o.cut.w + 'px'; c.style.height = o.cut.h + 'px';
        c.style.setProperty('--lift', (o.lift || 20) + 'px');
        c.style.setProperty('--lscale', o.lscale || 0.02);
        c.style.setProperty('--mr-bob', Math.round((o.lift || 20) * 0.35) + 'px');
        var lift = h('div', 'mr-lift'), bob = h('div', 'mr-bob');
        var im = h('img', 'mr-cut-sd');
        im.alt = ''; im.decoding = 'async';
        im.dataset.src = R(o.cut.src);
        rec.sd = im;
        bob.appendChild(im);
        if (o.cut.hd) {
          var hd = h('img', 'mr-cut-hd');
          hd.alt = ''; hd.decoding = 'async';
          hd.dataset.src = R(o.cut.hd);
          hd.addEventListener('load', function () { c.classList.add('hd-ready'); });
          bob.appendChild(hd);
          rec.hd = hd;
        }
        lift.appendChild(bob); c.appendChild(lift);
        self.layCut.appendChild(c);
        rec.cut = c;
      }
      if (o.path && rec.interactive) {
        var g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        var p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        p.setAttribute('d', o.path);
        p.setAttribute('data-obj', id);
        g.appendChild(p);
        self.svg.appendChild(g);
        rec.hitG = g; rec.hitPath = p;
      }
      self.objEls[id] = rec;
    });
    // hit order: smaller objects on top
    var paths = Object.keys(this.objEls).map(function (k) { return self.objEls[k]; }).filter(function (r) { return r.hitG; });
    paths.sort(function (a, b) { return (b.o.bbox[2] * b.o.bbox[3]) - (a.o.bbox[2] * a.o.bbox[3]); });
    paths.forEach(function (r) { self.svg.appendChild(r.hitG); });
  };

  Room.prototype._hotspotFor = function (objId) {
    var hs = this.content.hotspots;
    for (var i = 0; i < hs.length; i++) if (hs[i].object === objId) return hs[i];
    return null;
  };

  Room.prototype._renderIntro = function () {
    var s = this.content.settings, touch = !this.mqHover.matches;
    this.intro.innerHTML =
      (s.eyebrow ? '<p class="mr-eyebrow-intro">' + esc(s.eyebrow) + '</p>' : '') +
      (s.title ? '<h2 class="mr-intro-title">' + esc(s.title) + '</h2>' : '') +
      ((touch ? s.subtitleTouch : s.subtitle) ? '<p class="mr-intro-sub">' + esc(touch ? s.subtitleTouch : s.subtitle) + '</p>' : '');
    var hint = touch ? s.hintTouch : s.hint;
    this.hint.innerHTML = hint ? '<i></i>' + esc(hint.replace('{n}', this.content.hotspots.length)) : '';
    this.hint.hidden = !hint;
    // which objects are clickable
    var self = this;
    Object.keys(this.objEls).forEach(function (id) {
      var r = self.objEls[id];
      if (r.hitPath) r.hitPath.style.pointerEvents = self._hotspotFor(id) ? '' : 'none';
    });
  };

  Room.prototype._renderHotspots = function () {
    var self = this;
    this.hsLayer.innerHTML = '';
    this.hsEls = {};
    this.content.hotspots.forEach(function (hs) {
      var b = h('button', 'mr-hs');
      b.type = 'button';
      b.setAttribute('aria-label', hs.label);
      b.dataset.id = hs.id;
      b.innerHTML = '<span class="mr-hs-ring"></span><span class="mr-hs-ring r2"></span><span class="mr-hs-core"></span><span class="mr-hs-label">' + esc(hs.label) + '</span>';
      self.hsLayer.appendChild(b);
      self.hsEls[hs.id] = b;
    });
    this._layoutHotspots();
  };

  Room.prototype._depthAt = function (u, v) {
    var g = this.scene.depthGrid;
    if (!g) return this.focus;
    if (!this._dg) {
      var bin = atob(g.data), arr = new Uint8Array(bin.length);
      for (var i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      this._dg = arr;
    }
    var x = clamp(u, 0, 1) * (g.w - 1), y = clamp(v, 0, 1) * (g.h - 1);
    var x0 = Math.floor(x), y0 = Math.floor(y), x1 = Math.min(x0 + 1, g.w - 1), y1 = Math.min(y0 + 1, g.h - 1);
    var fx = x - x0, fy = y - y0, d = this._dg;
    var a = lerp(d[y0 * g.w + x0], d[y0 * g.w + x1], fx), b = lerp(d[y1 * g.w + x0], d[y1 * g.w + x1], fx);
    return lerp(a, b, fy) / 255;
  };

  /* ---------- loading ---------- */
  Room.prototype._load = function () {
    var self = this, sc = this.scene, R = this.resolve;
    var loadImg = function (src, cors) {
      return new Promise(function (res, rej) {
        var im = new Image();
        if (cors) im.crossOrigin = 'anonymous';
        im.decoding = 'async';
        im.onload = function () { res(im); };
        im.onerror = function () { rej(new Error('img ' + src)); };
        im.src = src;
      });
    };
    var done = 0, total = 2;
    var tick = function () { done++; self.loaderBar.style.width = Math.round(done / total * 100) + '%'; };
    var glOK = false;
    // Touch devices take the plain image path: no WebGL context. Real iPhones closed
    // the page over it (Safari's GPU process), and the depth-of-field it adds is
    // barely visible on a small screen. Keep in sync with the depth preload's media.
    if (this.mqHover.matches) {
      try { this.glv = new GLView(this.canvas); glOK = true; } catch (e) { this.glv = null; }
    }
    // base: list of resolutions -> load the small one first, then upgrade
    var bases = Array.isArray(sc.base) ? sc.base.slice().sort(function (a, b) { return a.size - b.size; }) : [{ src: sc.base, size: sc.w }];
    var dprW = (this.rw || 800) * Math.min(window.devicePixelRatio || 1, 2);
    var maxTex = 4096;
    if (this.glv) { try { maxTex = this.glv.gl.getParameter(this.glv.gl.MAX_TEXTURE_SIZE) || 4096; } catch (_) {} }
    var usable = bases.filter(function (b) { return b.size <= maxTex; });
    if (!usable.length) usable = [bases[0]];
    var first = usable[0];
    // the largest one is only worth it on large / dense screens
    var best = usable[usable.length - 1];
    if (dprW < 1400) best = first;
    var useBase = function (im) {
      if (self.glv && self.depthImg) {
        try {
          self.glv.texture(0, im, true, true);
          self.glReady = true;
          self.root.classList.add('gl-ready');
        } catch (e) { self.glReady = false; }
      }
      // same CORS mode as the loader, so the <img> reuses the cached response
      if (im.crossOrigin) self.baseImg.crossOrigin = im.crossOrigin;
      self.baseImg.src = im.src;
      self.baseImg.classList.remove('is-preview');
      self._needs = true; self._kick();
    };
    var baseP = loadImg(R(first.src), true).then(function (im) { tick(); return im; });
    var depthP = glOK ? loadImg(R(sc.depth), true).then(function (im) { tick(); return im; }) : Promise.resolve(null).then(function () { tick(); return null; });
    Promise.all([baseP, depthP]).then(function (res) {
      var base = res[0], depth = res[1];
      if (self.glv && depth) {
        try { self.glv.texture(1, depth, false, false); self.depthImg = depth; } catch (e) { self.depthImg = null; }
      }
      useBase(base);
      self._ready();
      if (best !== first) {
        // the sharper base (1.8MB) only matters once the camera zooms in, so it
        // waits for the first object to be opened
        self._loadBest = function () {
          self._loadBest = null;
          loadImg(R(best.src), glOK).then(function (im) {
            var apply = function () { if (self.root.isConnected !== false) useBase(im); };
            if (im.decode) im.decode().then(apply, apply); else apply();
          }, function () {});
        };
      }
    }).catch(function () {
      // fall back to plain image
      loadImg(R(first.src), false).then(function (im) {
        self.baseImg.src = im.src; self.baseImg.classList.remove('is-preview'); self._ready();
      }, function () { self._ready(); });
    });
  };
  var setSrc = function (im) { if (im && !im.src && im.dataset.src) im.src = im.dataset.src; };
  Room.prototype._loadHD = function (id) {
    var r = this.objEls[id];
    if (!r) return;
    setSrc(r.under && r.under.img); setSrc(r.shadow && r.shadow.img);
    // HD cut-outs are for the zoomed-in camera, which iOS never gets (the shelf's alone
    // is 14MB decoded)
    if (!this.stillCam) setSrc(r.hd);
  };
  Room.prototype._ready = function () {
    var self = this;
    this.ready = true;
    // Cut-outs sit on top of the base, so they load now that it is drawn. Under and
    // shadow layers only show on hover / open: they load when the browser is idle or
    // on first hover. HD cut-outs load only on hover / open.
    var recs = Object.keys(this.objEls).map(function (k) { return self.objEls[k]; });
    recs.forEach(function (r) { setSrc(r.sd); });
    (window.requestIdleCallback || function (f) { return setTimeout(f, 1200); })(function () {
      recs.forEach(function (r) { setSrc(r.under && r.under.img); setSrc(r.shadow && r.shadow.img); });
    }, { timeout: 3000 });
    requestAnimationFrame(function () { self.root.classList.add('is-ready'); });
    this._kick();
    if (this._pendingOpen) { var id = this._pendingOpen; this._pendingOpen = null; this.open(id); }
  };

  /* ---------- geometry ---------- */
  Room.prototype._isMobile = function () { return this.mqMobile.matches; };
  Room.prototype._roomFrame = function () {
    var f = this.scene.room && this.scene.room.frame;
    return f ? { x: f[0], y: f[1], w: f[2], h: f[3] } : { x: 0, y: 0, w: this.W, h: this.H };
  };
  Room.prototype._fit = function () {
    var rw = this.rw, rh = this.rh, mob = this._isMobile(), f = this._roomFrame();
    if (mob) { var a = this._area(false); return Math.min((rw - 24) / f.w, a.h / f.h); }
    var pad = Math.max(24, rh * 0.05);
    return Math.min((rw - pad * 2) / f.w, (rh - pad * 2) / f.h);
  };
  Room.prototype._area = function (open) {
    var rw = this.rw, rh = this.rh;
    if (!open) {
      if (this._isMobile()) {
        var ib = this.intro.textContent.trim() ? this.intro.offsetTop + this.intro.offsetHeight + 8 : 12;
        var ht = (!this.hint.hidden && this.hint.offsetHeight) ? this.hint.offsetTop - 8 : rh - 12;
        if (ht - ib < rh * 0.45) { ib = rh * 0.06; ht = rh * 0.98; }
        return { x: 0, y: ib, w: rw, h: ht - ib };
      }
      return { x: 0, y: 0, w: rw, h: rh };
    }
    if (this._isMobile()) {
      // frame the object above the half-open sheet
      return { x: 0, y: 0, w: rw, h: Math.max(140, rh * (1 - SHEET_PEEK)) };
    }
    var pw = (this.panel.offsetWidth || 420) + 40;
    return { x: 0, y: 0, w: Math.max(200, rw - pw), h: rh };
  };
  Room.prototype._homeCam = function () {
    var a = this._area(false), s = this._fit(), vx = a.x + a.w / 2, vy = a.y + a.h * (this._isMobile() ? 0.46 : 0.5);
    if (!this._isMobile() && this.intro.offsetWidth && this.intro.textContent.trim()) {
      // keep the scene clear of the intro copy when there is room for it
      var free = (this.rw - this._roomFrame().w * s) / 2;
      var introR = this.intro.offsetLeft + this.intro.offsetWidth + 28;
      vx += clamp(introR - free, 0, Math.max(0, free - 12));
    }
    var f = this._roomFrame();
    return { cx: f.x + f.w / 2, cy: f.y + f.h / 2, s: s, vx: vx, vy: vy };
  };
  Room.prototype._camFor = function (hs) {
    if (this.stillCam) return this._homeCam();
    var a = this._area(true), fit = this._fit(), mob = this._isMobile();
    var o = hs.object && this.scene.objects[hs.object];
    var bx, by, bw, bh;
    if (o) { bx = o.bbox[0]; by = o.bbox[1]; bw = o.bbox[2]; bh = o.bbox[3]; }
    else { bw = bh = this.W * 0.16; bx = hs.x / 100 * this.W - bw / 2; by = hs.y / 100 * this.H - bh / 2; }
    var fill = mob ? 0.66 : 0.58;
    var s = Math.min(a.w * fill / bw, a.h * fill / bh);
    if (hs.zoom) s = fit * hs.zoom;
    var maxS = mob ? 1.05 : 1.32;
    s = clamp(s, fit * 1.3, Math.max(maxS, fit * 1.3));
    var lift = o ? (o.lift || 20) * 0.7 : 0;
    return { cx: bx + bw / 2, cy: by + bh / 2 - lift, s: s, vx: a.x + a.w / 2, vy: a.y + a.h / 2 + (mob ? 6 : 0) };
  };
  Room.prototype._tweenTo = function (target, dur) {
    if (this.mqReduced.matches) dur = 1;
    this.tw = { from: Object.assign({}, this.cam), to: target, t0: performance.now(), dur: dur || 1100 };
    this._kick();
  };

  Room.prototype._resize = function (initial) {
    var r = this.root.getBoundingClientRect();
    this.rw = Math.max(1, r.width); this.rh = Math.max(1, r.height);
    var dpr = Math.min(window.devicePixelRatio || 1, this._isMobile() ? 2 : 2);
    var cw = Math.round(this.rw * dpr), ch = Math.round(this.rh * dpr);
    if (this.canvas.width !== cw || this.canvas.height !== ch) { this.canvas.width = cw; this.canvas.height = ch; }
    var target = this.active ? this._camFor(this.active) : this._homeCam();
    if (initial || !this.tw) { this.cam = target; this.tw = null; }
    else this.tw.to = target;
    var home = this._homeCam();
    this.root.style.setProperty('--mr-glow-x', (home.vx / this.rw * 100).toFixed(1) + '%');
    this._renderIntro();
    this._kick();
  };

  /* ---------- interaction ---------- */
  Room.prototype._bind = function () {
    var self = this;
    this._onResize = function () { self._resize(false); };
    window.addEventListener('resize', this._onResize);
    if (window.ResizeObserver) { this._ro = new ResizeObserver(this._onResize); this._ro.observe(this.root); }
    if (window.IntersectionObserver) {
      this._io = new IntersectionObserver(function (en) { self.visible = en[0].isIntersecting; if (self.visible) self._kick(); }, { threshold: 0 });
      this._io.observe(this.root);
    }
    document.addEventListener('visibilitychange', function () { if (!document.hidden) self._kick(); });

    this.root.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse') return;
      var r = self.root.getBoundingClientRect();
      self.mouse.x = ((e.clientX - r.left) / r.width) * 2 - 1;
      self.mouse.y = ((e.clientY - r.top) / r.height) * 2 - 1;
      self.mouse.in = true;
      self._kick();
    });
    this.root.addEventListener('pointerleave', function () { self.mouse.in = false; self._kick(); });

    // object hover / click
    this.svg.addEventListener('pointerover', function (e) {
      var id = e.target.getAttribute && e.target.getAttribute('data-obj');
      if (id && self.mqHover.matches) self._setHover(id);
    });
    this.svg.addEventListener('pointerout', function (e) {
      var id = e.target.getAttribute && e.target.getAttribute('data-obj');
      if (id && self.hover === id) self._setHover(null);
    });
    this.svg.addEventListener('click', function (e) {
      var id = e.target.getAttribute && e.target.getAttribute('data-obj');
      if (!id) return;
      e.stopPropagation();
      var hs = self._hotspotFor(id);
      if (hs) self.open(hs.id);
    });
    this.viewport.addEventListener('click', function (e) {
      if (e.target.closest('.mr-hs') || (e.target.getAttribute && e.target.getAttribute('data-obj'))) return;
      if (self.active && !self.editable) self.close();
    });

    // hotspots
    this.hsLayer.addEventListener('pointerover', function (e) {
      var b = e.target.closest('.mr-hs'); if (!b || !self.mqHover.matches) return;
      var hs = self._hs(b.dataset.id); if (hs && hs.object) self._setHover(hs.object);
    });
    this.hsLayer.addEventListener('pointerout', function (e) {
      var b = e.target.closest('.mr-hs'); if (!b) return;
      if (b.contains(e.relatedTarget)) return;
      var hs = self._hs(b.dataset.id); if (hs && self.hover === hs.object) self._setHover(null);
    });
    this.hsLayer.addEventListener('click', function (e) {
      var b = e.target.closest('.mr-hs'); if (!b) return;
      e.stopPropagation();
      if (self._dragMoved) { self._dragMoved = false; return; }
      self.open(b.dataset.id);
    });
    if (this.editable) this._bindDrag();

    // panel
    this.panel.querySelector('.mr-close').addEventListener('click', function () { self.close(); });
    this.panel.querySelector('.mr-prev').addEventListener('click', function () { self.step(-1); });
    this.panel.querySelector('.mr-next').addEventListener('click', function () { self.step(1); });
    this._onKey = function (e) {
      if (!self.active) return;
      var tag = (e.target && e.target.tagName) || '';
      if (/INPUT|TEXTAREA|SELECT/.test(tag) || (e.target && e.target.isContentEditable)) return;
      if (e.key === 'Escape') self.close();
      else if (e.key === 'ArrowRight') self.step(1);
      else if (e.key === 'ArrowLeft') self.step(-1);
    };
    document.addEventListener('keydown', this._onKey);
    this._bindSheetDrag();
  };

  // Mobile bottom sheet: it opens half way (SHEET_PEEK) and behaves like a native one.
  // - A swipe up moves the sheet with the finger; once it is fully up the same swipe
  //   goes on scrolling the content, with a glide on release.
  // - A wheel / trackpad scroll down on a half open sheet expands it, and the rest of
  //   that scroll lands in the content.
  // - Expanded, the content scrolls natively. Pulling down (or scrolling up) from its
  //   top collapses the sheet; the grip / top bar still drag it from anywhere.
  // - Dragging a half open sheet down closes it, as does collapsing a full one (video).
  // Touch runs on touch events: the first vertical move claims the gesture with a
  // preventDefault, so the browser never takes it over for a page scroll (Android
  // Chrome cancels pointer events as soon as it does). Mouse and pen use pointers.
  Room.prototype._bindSheetDrag = function () {
    var self = this, panel = this.panel, root = this.root, scroller = this.panelScroll;
    var dragged = false, glideId = 0;
    var setDrag = function (px) { root.style.setProperty('--mr-sheet-drag', px + 'px'); };
    var isExpanded = function () { return root.classList.contains('is-expanded'); };
    var isFull = function () { return !!(self.active && self.active.full); };
    var markDragged = function () {
      dragged = true;
      setTimeout(function () { dragged = false; }, 60);
    };
    var collapse = function () {
      if (isFull()) { self.close(); return; }
      root.classList.remove('is-expanded');
      scroller.scrollTop = 0;
    };
    var stopGlide = function () { if (glideId) cancelAnimationFrame(glideId); glideId = 0; };
    // v in px/ms, decaying like a native fling
    var glide = function (v) {
      var last = 0;
      var step = function (now) {
        var dt = last ? Math.min(now - last, 40) : 16;
        last = now;
        scroller.scrollTop += v * dt;
        v *= Math.pow(0.95, dt / 16);
        glideId = Math.abs(v) > 0.02 && isExpanded() ? requestAnimationFrame(step) : 0;
      };
      glideId = requestAnimationFrame(step);
    };

    // One gesture, whatever the input. begin() returns null when the sheet has no
    // business with it; expanded, only the bar or a pull from the content's top drags.
    var begin = function (x, y, target) {
      stopGlide();
      if (!self._isMobile() || !self.active) return null;
      if (target.closest('button, a, video, iframe')) return null;
      var expanded = isExpanded();
      var onBar = !!target.closest('.mr-panel-bar, .mr-panel-grip');
      if (expanded && !onBar && scroller.scrollTop > 0) return null;
      return { x: x, y: y, dy: 0, on: false, expanded: expanded, pullOnly: expanded && !onBar, scroll: null };
    };
    // false: not decided yet; null: not ours (let it be); true: the sheet follows it
    var move = function (g, x, y, t) {
      var dx = x - g.x, dy = y - g.y;
      if (!g.on) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return false;
        if (Math.abs(dx) > Math.abs(dy)) return null; // sideways: gallery swipe
        if (g.pullOnly && (dy < 0 || scroller.scrollTop > 0)) return null; // content scroll
        g.on = true;
        panel.classList.add('is-dragging');
      }
      var room = g.expanded ? 0 : panel.offsetHeight * SHEET_OFF;
      if (!g.expanded && !g.scroll && dy <= -room) {
        // fully up: expand now and hand the rest of this swipe to the content
        root.classList.add('is-expanded');
        setDrag(0);
        g.scroll = { y: y, top: scroller.scrollTop, lastY: y, lastT: t, v: 0 };
      }
      if (g.scroll) {
        var s = g.scroll, dt = t - s.lastT;
        scroller.scrollTop = s.top + (s.y - y);
        if (dt > 0) s.v = (s.lastY - y) / dt;
        s.lastY = y; s.lastT = t;
        return true;
      }
      g.dy = Math.max(-room, dy);
      setDrag(g.dy);
      return true;
    };
    var finish = function (g, t) {
      if (!g || !g.on) return;
      panel.classList.remove('is-dragging');
      setDrag(0);
      markDragged();
      if (g.scroll) {
        // a finger that stopped before lifting gets no glide
        if (t - g.scroll.lastT < 80 && Math.abs(g.scroll.v) > 0.1) glide(g.scroll.v);
        return;
      }
      if (!g.expanded && g.dy < -40) root.classList.add('is-expanded');
      else if (g.dy > 90) {
        if (g.expanded) collapse();
        else self.close();
      }
    };

    var tg = null;
    var touchOf = function (e) {
      for (var i = 0; i < e.changedTouches.length; i++) if (e.changedTouches[i].identifier === tg.id) return e.changedTouches[i];
      return null;
    };
    panel.addEventListener('touchstart', function (e) {
      var t = e.touches[0];
      tg = e.touches.length === 1 ? begin(t.clientX, t.clientY, e.target) : null;
      if (tg) tg.id = t.identifier;
    }, { passive: true });
    panel.addEventListener('touchmove', function (e) {
      if (!tg) return;
      var t = touchOf(e);
      if (!t) return;
      var r = move(tg, t.clientX, t.clientY, e.timeStamp);
      if (r === null) { tg = null; return; }
      if (r && e.cancelable) e.preventDefault();
    }, { passive: false });
    var touchEnd = function (e) {
      if (!tg || !touchOf(e)) return;
      var g = tg;
      tg = null;
      finish(g, e.timeStamp);
    };
    panel.addEventListener('touchend', touchEnd);
    panel.addEventListener('touchcancel', touchEnd);

    var pg = null;
    panel.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch' || e.button > 0) return;
      pg = begin(e.clientX, e.clientY, e.target);
      if (pg) pg.id = e.pointerId;
    });
    panel.addEventListener('pointermove', function (e) {
      if (!pg || e.pointerId !== pg.id) return;
      var was = pg.on, r = move(pg, e.clientX, e.clientY, e.timeStamp);
      if (r === null) { pg = null; return; }
      if (r && !was) try { panel.setPointerCapture(e.pointerId); } catch (_) {}
    });
    var pointerEnd = function (e) {
      if (!pg || e.pointerId !== pg.id) return;
      var g = pg;
      pg = null;
      finish(g, e.timeStamp);
    };
    panel.addEventListener('pointerup', pointerEnd);
    panel.addEventListener('pointercancel', pointerEnd);

    // Wheel / trackpad. A pause over 200ms starts a new gesture, so the momentum of a
    // scroll that reached the top never collapses the sheet; only a fresh scroll up does.
    var wheel = null, lastWheel = 0;
    panel.addEventListener('wheel', function (e) {
      if (!self._isMobile() || !self.active) return;
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // sideways: gallery
      var fresh = e.timeStamp - lastWheel > 200;
      lastWheel = e.timeStamp;
      if (!isExpanded()) {
        e.preventDefault();
        if (e.deltaY > 0) root.classList.add('is-expanded');
        return;
      }
      if (isFull()) return;
      if (fresh) wheel = { atTop: scroller.scrollTop <= 0, sum: 0 };
      if (!wheel || !wheel.atTop || e.deltaY > 0) { wheel = null; return; }
      wheel.sum += e.deltaY;
      if (wheel.sum < -60) { wheel = null; collapse(); }
    }, { passive: false });
    // a drag that ends on a link or button must not also click it
    panel.addEventListener('click', function (e) { if (dragged) { e.preventDefault(); e.stopPropagation(); } }, true);
  };

  Room.prototype._bindDrag = function () {
    var self = this, drag = null;
    this.hsLayer.addEventListener('pointerdown', function (e) {
      var b = e.target.closest('.mr-hs'); if (!b) return;
      drag = { id: b.dataset.id, x0: e.clientX, y0: e.clientY, moved: false, b: b };
      b.setPointerCapture(e.pointerId);
      e.preventDefault();
    });
    this.hsLayer.addEventListener('pointermove', function (e) {
      if (!drag) return;
      if (!drag.moved && Math.hypot(e.clientX - drag.x0, e.clientY - drag.y0) < 4) return;
      drag.moved = true; drag.b.classList.add('is-dragging');
      var r = self.root.getBoundingClientRect(), c = self.cam;
      var px = (e.clientX - r.left - c.vx) / c.s + c.cx, py = (e.clientY - r.top - c.vy) / c.s + c.cy;
      var hs = self._hs(drag.id);
      hs.x = Math.round(clamp(px / self.W * 100, 0, 100) * 10) / 10;
      hs.y = Math.round(clamp(py / self.H * 100, 0, 100) * 10) / 10;
      self._layoutHotspots();
      if (self.onChange) self.onChange(hs, 'position');
    });
    var end = function () {
      if (!drag) return;
      drag.b.classList.remove('is-dragging');
      self._dragMoved = drag.moved;
      drag = null;
    };
    this.hsLayer.addEventListener('pointerup', end);
    this.hsLayer.addEventListener('pointercancel', end);
  };

  Room.prototype._hs = function (id) {
    var hs = this.content.hotspots;
    for (var i = 0; i < hs.length; i++) if (hs[i].id === id) return hs[i];
    return null;
  };

  Room.prototype._setHover = function (id) {
    if (this.hover === id) return;
    var prev = this.hover; this.hover = id;
    if (prev && (!this.active || this.active.object !== prev)) this._objState(prev, null);
    if (!this.active) this.dimT = id && !this.editable ? 0.22 : 0;
    if (id && (!this.active || this.active.object !== id)) this._objState(id, 'hover');
    var self = this;
    Object.keys(this.hsEls).forEach(function (k) {
      var hs = self._hs(k);
      self.hsEls[k].classList.toggle('is-hover', !!(hs && id && hs.object === id));
    });
    this.root.classList.add('has-interacted');
    this._needs = true; this._kick();
  };

  Room.prototype._objState = function (id, state) {
    var r = this.objEls[id]; if (!r) return;
    if (state) this._loadHD(id);
    if (r.cut) { r.cut.classList.toggle('is-hover', state === 'hover'); r.cut.classList.toggle('is-active', state === 'active'); }
    if (r.under) r.under.img.classList.toggle('is-on', !!state);
    if (r.shadow) { r.shadow.img.classList.toggle('is-hover', state === 'hover'); r.shadow.img.classList.toggle('is-active', state === 'active'); }
  };

  Room.prototype._setOccluders = function (objId) {
    var self = this;
    Object.keys(this.objEls).forEach(function (k) { var r = self.objEls[k]; if (r.cut) r.cut.classList.remove('is-occluder'); });
    var o = objId && this.scene.objects[objId];
    if (o && o.occluders) o.occluders.forEach(function (k) { var r = self.objEls[k]; if (r && r.cut) r.cut.classList.add('is-occluder'); });
  };

  /* ---------- open / close ---------- */
  Room.prototype.open = function (id) {
    var hs = this._hs(id);
    if (!hs) return;
    if (!this.ready) { this._pendingOpen = id; return; }
    if (this._loadBest) this._loadBest();
    var prev = this.active;
    if (prev && prev.object && prev.object !== hs.object) this._objState(prev.object, null);
    this.active = hs;
    this.root.classList.add('is-open', 'has-interacted');
    this.root.classList.toggle('has-object', !!hs.object);
    // video pop-ups open full screen on mobile; the rest open half way
    this.root.classList.toggle('is-full', hs.full);
    this.root.classList.toggle('is-expanded', hs.full);
    this.dimT = hs.object ? 1 : 0.55;
    if (hs.object) this._objState(hs.object, 'active');
    this._setOccluders(hs.object);
    var self = this;
    Object.keys(this.hsEls).forEach(function (k) { self.hsEls[k].classList.toggle('is-active', k === id); });
    this._renderPanel(hs);
    // panel geometry is known after class toggle
    if (!this.stillCam) this._tweenTo(this._camFor(hs), prev ? 950 : 1150);
    this.focusT = hs.object ? (this.scene.objects[hs.object].depth || this.focus) : this._depthAt(hs.x / 100, hs.y / 100);
    if (!prev) this._returnFocus = document.activeElement;
    var closeBtn = this.panel.querySelector('.mr-close');
    setTimeout(function () { try { closeBtn.focus({ preventScroll: true }); } catch (_) {} }, 60);
    if (this.onOpen) this.onOpen(hs);
  };

  Room.prototype.close = function () {
    if (!this.active) return;
    var hs = this.active;
    if (hs.object) this._objState(hs.object, this.hover === hs.object ? 'hover' : null);
    this._setOccluders(null);
    this.active = null;
    this.root.classList.remove('is-open', 'has-object', 'is-full', 'is-expanded');
    this.dimT = 0;
    var self = this;
    Object.keys(this.hsEls).forEach(function (k) { self.hsEls[k].classList.remove('is-active'); });
    this._pauseMedia();
    if (!this.stillCam) this._tweenTo(this._homeCam(), 1000);
    this.focusT = this.scene.focus || 0.55;
    var rf = this._returnFocus; this._returnFocus = null;
    if (rf && rf.focus && this.root.contains(rf)) try { rf.focus({ preventScroll: true }); } catch (_) {}
    if (this.onClose) this.onClose(hs);
  };

  Room.prototype.step = function (d) {
    var hs = this.content.hotspots; if (!hs.length) return;
    var i = this.active ? hs.indexOf(this.active) : -1;
    var n = hs[(i + d + hs.length) % hs.length];
    this.open(n.id);
  };

  Room.prototype._pauseMedia = function () {
    this.panel.querySelectorAll('video').forEach(function (v) { try { v.pause(); } catch (_) {} });
    this.panel.querySelectorAll('iframe').forEach(function (f) { var s = f.src; f.src = 'about:blank'; f.dataset.src = s; });
  };

  Room.prototype._renderPanel = function (hs) {
    var self = this, S = this.strings, list = this.content.hotspots, idx = list.indexOf(hs) + 1;
    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    this.panelCount.innerHTML = '<b>' + pad(idx) + '</b> ' + S.of + ' ' + pad(list.length);
    var titleId = this.panel.getAttribute('aria-labelledby');
    var html = '';
    if (hs.eyebrow) html += '<p class="mr-eyebrow">' + esc(hs.eyebrow) + '</p>';
    html += '<h3 class="mr-title" id="' + esc(titleId) + '">' + esc(hs.title || hs.label) + '</h3>';
    var media = hs.media.slice();
    // a full-screen pop-up still waiting for its video starts with a grey placeholder
    if (hs.full && !media.some(function (m) { return /^(video|youtube|vimeo)$/.test(m.type); })) media.unshift({ type: 'placeholder' });
    if (media.length) {
      html += '<div class="mr-gallery' + (media.length < 2 ? ' is-single' : '') + '"><div class="mr-track">';
      media.forEach(function (m, i) {
        var cap = m.caption ? '<figcaption>' + esc(m.caption) + '</figcaption>' : '';
        var t = m.type || 'image', src = m.src || m.url;
        // muted autoplay only while the pop-up is open, never with reduced motion
        var autoplay = !!m.autoplay && !self.mqReduced.matches;
        if (t === 'placeholder') {
          html += '<figure class="mr-slide is-placeholder"></figure>';
        } else if (t === 'video') {
          if (m.msrc && self._isMobile()) src = m.msrc;
          // native HLS (Safari, every iOS browser) adapts the size to the pop-up
          if (m.hls && document.createElement('video').canPlayType('application/vnd.apple.mpegurl')) src = m.hls;
          html += '<figure class="mr-slide is-video"><video src="' + esc(src) + '" ' + (m.poster ? 'poster="' + esc(m.poster) + '" ' : '') +
            (m.controls ? 'controls ' : '') + 'playsinline preload="metadata"' + (m.loop ? ' loop' : '') + ((m.muted || autoplay) ? ' muted' : '') +
            (autoplay ? ' autoplay data-autoplay' : '') + '></video>' + cap +
            '<button type="button" class="mr-fs" aria-label="' + esc(S.fullscreen) + '">' + ICON.fullscreen + '</button></figure>';
        } else if (t === 'youtube' || t === 'vimeo') {
          var id = t === 'youtube' ? youtubeId(src) : vimeoId(src);
          var url = t === 'youtube' ? 'https://www.youtube-nocookie.com/embed/' + id + '?rel=0&playsinline=1&modestbranding=1' : 'https://player.vimeo.com/video/' + id + '?dnt=1';
          html += '<figure class="mr-slide is-video"><iframe src="' + esc(url) + '" title="' + esc(m.caption || S.video) + '" loading="lazy" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe></figure>';
        } else {
          // each image keeps its own ratio (and its mobile crop its own); known sizes
          // reserve the space before it loads
          var ratio = (m.w && m.h ? '--ar:' + (+m.w) + '/' + (+m.h) + ';' : '') + (m.mw && m.mh ? '--ar-m:' + (+m.mw) + '/' + (+m.mh) + ';' : '');
          html += '<figure class="mr-slide is-image"' + (ratio ? ' style="' + ratio + '"' : '') + '><picture>' +
            (m.msrcset ? '<source media="' + MOBILE_MQ + '" srcset="' + esc(m.msrcset) + '" sizes="92vw">' : '') +
            '<img src="' + esc(src) + '"' + (m.srcset ? ' srcset="' + esc(m.srcset) + '" sizes="(max-width: 760px) 92vw, 360px"' : '') +
            ' alt="' + esc(m.alt || '') + '" loading="' + (i ? 'lazy' : 'eager') + '" decoding="async"></picture>' + cap + '</figure>';
        }
      });
      html += '</div><div class="mr-gal-ui"><div class="mr-dots">';
      media.forEach(function (m, i) { html += '<button type="button" aria-label="' + (i + 1) + '"' + (i ? '' : ' aria-current="true"') + '></button>'; });
      html += '</div><div class="mr-gal-arrows"><button type="button" class="mr-iconbtn" data-dir="-1" aria-label="' + esc(S.prev) + '">' + ICON.prev + '</button>' +
        '<button type="button" class="mr-iconbtn" data-dir="1" aria-label="' + esc(S.next) + '">' + ICON.next + '</button></div></div></div>';
    }
    if (hs.body) html += '<div class="mr-body">' + hs.body + '</div>';
    if (hs.ctas.length) {
      html += '<div class="mr-ctas">';
      hs.ctas.forEach(function (b, i) {
        var ext = /^https?:/i.test(b.url || '') ? ' target="_blank" rel="noopener"' : '';
        html += '<a class="mr-cta' + (i === 0 ? ' is-primary' : '') + '" href="' + esc(b.url || '#') + '"' + ext + '>' + esc(b.label) + (i === 0 ? ICON.arrow : '') + '</a>';
      });
      html += '</div>';
    }
    this._pauseMedia();
    this.panelScroll.innerHTML = html;
    this.panelScroll.scrollTop = 0;
    this._bindGallery();
    this._bindFullscreen();
  };

  // Full screen for pop-up videos, with sound and the native controls while it lasts.
  // iPhone Safari only takes a <video> full screen (webkitEnterFullscreen, whose player
  // always shows its own controls); everything else uses the Fullscreen API. Controls
  // and sound only switch on once full screen has really started, so a refused request
  // leaves the inline loop untouched, and leaving restores it muted and control-free.
  Room.prototype._bindFullscreen = function () {
    this.panel.querySelectorAll('.mr-fs').forEach(function (btn) {
      var v = btn.parentNode.querySelector('video');
      if (!v) return;
      var inline = { controls: v.controls, muted: v.muted };
      var restore = function () { v.controls = inline.controls; v.muted = inline.muted; };
      var sync = function () {
        if ((document.fullscreenElement || document.webkitFullscreenElement) === v) { v.controls = true; v.muted = false; }
        else restore();
      };
      v.addEventListener('fullscreenchange', sync);
      v.addEventListener('webkitfullscreenchange', sync);
      v.addEventListener('webkitendfullscreen', restore);
      btn.addEventListener('click', function () {
        var p = v.play(); if (p && p.catch) p.catch(function () {});
        if (document.fullscreenEnabled && v.requestFullscreen) v.requestFullscreen().catch(function () {});
        else if (v.webkitEnterFullscreen) {
          // unmuted inside the tap: iOS only allows sound from a user gesture
          v.muted = false;
          v.webkitEnterFullscreen();
        } else if (v.webkitRequestFullscreen) v.webkitRequestFullscreen();
      });
    });
  };

  Room.prototype._bindGallery = function () {
    var g = this.panelScroll.querySelector('.mr-gallery'); if (!g) return;
    var track = g.querySelector('.mr-track'), dots = g.querySelectorAll('.mr-dots button');
    var cur = 0;
    var go = function (i) {
      i = clamp(i, 0, dots.length - 1);
      track.scrollTo({ left: i * (track.clientWidth + 10), behavior: 'smooth' });
    };
    track.addEventListener('scroll', function () {
      var i = Math.round(track.scrollLeft / (track.clientWidth + 10));
      if (i !== cur) {
        cur = i;
        dots.forEach(function (d, k) { d.setAttribute('aria-current', k === i ? 'true' : 'false'); });
        track.querySelectorAll('video').forEach(function (v) { try { v.pause(); } catch (_) {} });
        // back on an autoplay video: carry on playing
        var slide = track.children[i], v = slide && slide.querySelector('video[data-autoplay]');
        if (v) { var pr = v.play(); if (pr && pr.catch) pr.catch(function () {}); }
      }
    }, { passive: true });
    dots.forEach(function (d, k) { d.addEventListener('click', function () { go(k); }); });
    g.querySelectorAll('.mr-gal-arrows button').forEach(function (b) {
      b.addEventListener('click', function () { go(cur + (+b.dataset.dir)); });
    });
  };

  /* ---------- render loop ---------- */
  Room.prototype._kick = function () {
    if (this.running) return;
    this.running = true;
    var self = this;
    this._last = performance.now();
    requestAnimationFrame(function f(t) { self._frame(t, f); });
  };

  Room.prototype._frame = function (t, f) {
    var dt = Math.min(0.05, (t - this._last) / 1000); this._last = t; this._t += dt;
    var busy = false;
    if (this.tw) {
      var k = clamp((t - this.tw.t0) / this.tw.dur, 0, 1), e = easeInOut(k), a = this.tw.from, b = this.tw.to;
      this.cam = {
        cx: lerp(a.cx, b.cx, e), cy: lerp(a.cy, b.cy, e),
        s: Math.exp(lerp(Math.log(a.s), Math.log(b.s), e)),
        vx: lerp(a.vx, b.vx, e), vy: lerp(a.vy, b.vy, e)
      };
      this._dolly = { x: (b.cx - this.cam.cx) / this.W, y: (b.cy - this.cam.cy) / this.H };
      if (k >= 1) { this.tw = null; this._dolly = null; }
      busy = true;
    }
    var st = this.content.settings, reduced = this.mqReduced.matches;
    // No parallax on iOS: on touch screens it drifts the scene every frame for as long
    // as the page is open, the same per-frame movement that got iPhones killed.
    var amp = (st.parallax && !reduced && !this.editable && !this.stillCam) ? 0.011 * Math.sqrt(this._fit() / this.cam.s) : 0;
    var tx = 0, ty = 0, ambient = false;
    if (amp) {
      if (this.mqHover.matches) {
        if (this.mouse.in) { tx = -this.mouse.x * amp; ty = -this.mouse.y * amp * 0.7; }
      } else {
        ambient = true;
        tx = Math.sin(this._t * 0.45) * amp * 0.55;
        ty = Math.cos(this._t * 0.33) * amp * 0.35;
      }
      if (this._dolly) { tx -= this._dolly.x * 0.06; ty -= this._dolly.y * 0.06; }
    }
    var kk = 1 - Math.exp(-dt * 5);
    this.par.x += (tx - this.par.x) * kk; this.par.y += (ty - this.par.y) * kk;
    this.focus += (this.focusT - this.focus) * (1 - Math.exp(-dt * 3));
    this.dim += (this.dimT - this.dim) * (1 - Math.exp(-dt * 5));
    // iOS dims with a CSS fade and has no depth of field (WebGL): no need to ease these
    if (this.stillCam) { this.focus = this.focusT; this.dim = this.dimT; }
    if (Math.abs(this.dimT - this.dim) > 0.002) busy = true; else this.dim = this.dimT;
    if (Math.abs(tx - this.par.x) > 1e-5 || Math.abs(ty - this.par.y) > 1e-5 || Math.abs(this.focusT - this.focus) > 1e-4) busy = true;
    if (ambient) busy = true;
    this._apply();
    if ((busy || this._needs) && this.visible && !document.hidden) requestAnimationFrame(f);
    else this.running = false;
    this._needs = false;
  };

  Room.prototype._apply = function () {
    var c = this.cam, W = this.W, H = this.H, self = this;
    var mtx = 'matrix(' + c.s + ',0,0,' + c.s + ',' + (c.vx - c.cx * c.s) + ',' + (c.vy - c.cy * c.s) + ')';
    this.world.style.transform = mtx;
    this.worldBack.style.transform = mtx;
    this.floorWorld.style.transform = mtx;
    var act = this.active && this.active.object;
    if (Math.abs((this._lastS || 0) - c.s) > 0.01) { this._lastS = c.s; this.root.style.setProperty('--mr-scale', c.s.toFixed(3)); }
    if (Math.abs(this._lastDim - this.dim) > 0.004 || (this.dim === this.dimT && this._lastDim !== this.dim)) {
      this._lastDim = this.dim; this.root.style.setProperty('--mr-dim', this.dim.toFixed(3));
    }
    // per-object parallax
    var par = this.par, focus = this.focus;
    Object.keys(this.objEls).forEach(function (id) {
      var r = self.objEls[id], d = (r.o.depth != null ? r.o.depth : focus) - focus;
      if (id === act || (id === self.hover && !act)) d += 0.22;
      var tr = 'translate(' + (d * par.x * W).toFixed(2) + 'px,' + (d * par.y * H).toFixed(2) + 'px)';
      if (r.under) r.under.g.style.transform = tr;
      if (r.shadow) r.shadow.g.style.transform = tr;
      if (r.cut) r.cut.style.translate = (d * par.x * W).toFixed(2) + 'px ' + (d * par.y * H).toFixed(2) + 'px';
      if (r.hitG) r.hitG.setAttribute('transform', 'translate(' + (d * par.x * W).toFixed(2) + ' ' + (d * par.y * H).toFixed(2) + ')');
    });
    this._layoutHotspots();
    if (this.glReady) this.glv.draw(this.rw, this.rh, c, { w: W, h: H }, par, focus, this.dim, Math.max(0, this.dim - 0.25) / 0.75 * (this._isMobile() ? 1.3 : 1.9));
  };

  Room.prototype._layoutHotspots = function () {
    var c = this.cam, self = this, par = this.par, focus = this.focus;
    this.content.hotspots.forEach(function (hs) {
      var b = self.hsEls[hs.id]; if (!b) return;
      var u = hs.x / 100, v = hs.y / 100;
      if (hs._du !== u || hs._dv !== v) { hs._d = self._depthAt(u, v); hs._du = u; hs._dv = v; }
      var d = hs._d - focus;
      var px = u * self.W + d * par.x * self.W, py = v * self.H + d * par.y * self.H;
      var sx = (px - c.cx) * c.s + c.vx, sy = (py - c.cy) * c.s + c.vy;
      b.style.transform = 'translate3d(' + sx.toFixed(1) + 'px,' + sy.toFixed(1) + 'px,0)';
    });
  };

  /* ---------- public API ---------- */
  Room.prototype.setContent = function (content) {
    var activeId = this.active && this.active.id;
    this.content = normalizeContent(content);
    this._applyTheme();
    this._renderHotspots();
    this._renderIntro();
    if (activeId && this._hs(activeId)) { this.active = this._hs(activeId); this._renderPanel(this.active); }
    else if (activeId) { this.active = null; this.root.classList.remove('is-open'); this._tweenTo(this._homeCam(), 600); }
    this._needs = true; this._kick();
  };
  Room.prototype.rerender = function () {
    this._applyTheme();
    this._renderHotspots();
    this._renderIntro();
    if (this.active) {
      var b = this.hsEls[this.active.id]; if (b) b.classList.add('is-active');
      this._renderPanel(this.active);
    }
    this._needs = true; this._kick();
  };
  Room.prototype.refresh = function (hsId, what) {
    var hs = this._hs(hsId); if (!hs) return;
    if (what !== 'position' && this.active === hs) this._renderPanel(hs);
    var b = this.hsEls[hsId];
    if (b) { b.setAttribute('aria-label', hs.label); var l = b.querySelector('.mr-hs-label'); if (l) l.textContent = hs.label; }
    this._renderIntro();
    this._needs = true; this._kick();
  };
  Room.prototype.getContent = function () {
    return JSON.parse(JSON.stringify(this.content, function (k, v) { return k.charAt(0) === '_' ? undefined : v; }));
  };
  Room.prototype.destroy = function () {
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('keydown', this._onKey);
    if (this._ro) this._ro.disconnect();
    if (this._io) this._io.disconnect();
    this.visible = false; this.tw = null;
    this.root.classList.remove('is-editing', 'is-open', 'has-object', 'gl-ready', 'is-ready', 'has-interacted', 'labels-on');
    this.root.innerHTML = '';
  };

  Room.normalizeContent = normalizeContent;
  global.MellerRoom = Room;
})(window);
